/**
 * @file     DocumentsPanel.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Selectable due-diligence checklist and document-package request CTA.
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { useLeadCapture, type ListingContext } from '@/components/lead-capture/LeadCaptureContext';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import type { DocumentRecommendation } from './documentRecommendations';

interface Props {
  listing: ListingContext;
  recommendations: DocumentRecommendation[];
  analyticsSurface: 'slug_page' | 'map_sheet' | 'quick_view';
  /** Drop the heading when a titled ListingSection wraps the panel. */
  headless?: boolean;
}

export function DocumentsPanel({ listing, recommendations, analyticsSurface, headless = false }: Props) {
  const { t } = useTranslation();
  const { open } = useLeadCapture();
  const [selected, setSelected] = useState(() => new Set(recommendations.map((document) => document.id)));
  const selectedDocuments = recommendations.filter((document) => selected.has(document.id));

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box component={headless ? 'div' : 'section'} aria-labelledby={headless ? undefined : 'documents-heading'}>
      {headless ? null : (
        <Typography id="documents-heading" variant="h4" component="h2" sx={{ fontWeight: 600 }}>
          {t('listingDetail.documents.title', { defaultValue: 'Documents and due diligence' })}
        </Typography>
      )}
      <Typography sx={{ mt: headless ? 0 : 1, fontSize: 15, lineHeight: 1.55, color: hubColors.textSecondary, maxWidth: '68ch' }}>
        {t('listingDetail.documents.intro', {
          defaultValue: 'Pick the records you want. Ray Rasouli will email what the listing brokerage shares.',
        })}
      </Typography>

      <Box component="ul" sx={{ m: 0, mt: 3, p: 0, listStyle: 'none' }}>
        {recommendations.map((document) => {
          const checked = selected.has(document.id);
          return (
            <Box component="li" key={document.id} sx={{ borderBottom: hairline }}>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  minHeight: 52,
                  py: 2,
                  cursor: 'pointer',
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => toggle(document.id)}
                  inputProps={{ 'aria-label': document.label }}
                  sx={{ p: 1, ml: -1, color: hubColors.borderStrong, '&.Mui-checked': { color: hubColors.interactivePrimary } }}
                />
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 4, rowGap: 0.5 }}>
                  <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary }}>{document.label}</Box>
                  <Box component="span" sx={{ fontSize: 14, color: hubColors.textSecondary, textAlign: { xs: 'start', sm: 'end' } }}>{document.reason}</Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
        <Button
          variant="contained"
          disableElevation
          disabled={selectedDocuments.length === 0}
          onClick={() =>
            open({
              mode: 'documents',
              listing,
              documentOptions: recommendations.map((document) => ({ id: document.id, label: document.label })),
              requestedDocuments: selectedDocuments.map((document) => ({ id: document.id, label: document.label })),
              analyticsSurface,
            })
          }
          sx={{ minHeight: 44, px: `${layoutTokens.button.md.paddingX}px`, borderRadius: `${layoutTokens.radius.md}px`, fontWeight: fontWeightTokens.medium }}
        >
          {t('listingDetail.documents.request', {
            defaultValue: 'Request selected documents ({{count}})',
            count: selectedDocuments.length,
          })}
        </Button>
        <Typography sx={{ fontSize: 13, color: hubColors.textSecondary }}>
          {t('listingDetail.documents.note', {
            defaultValue: 'Availability is not guaranteed. Review documents with a professional.',
          })}
        </Typography>
      </Box>
    </Box>
  );
}
